// Thin client that exposes the same shape the Base44 SDK exposed (so we
// don't have to rewrite every call site), backed by fetch against the
// Cloudflare Workers API.
//
//   base44.entities.Services.filter({slug: "kitchen", is_active: true})
//     → GET /api/services?slug=kitchen&is_active=true
//
//   base44.entities.PortfolioProjects.list("-completion_date", 6)
//     → GET /api/portfolio-projects?orderBy=-completion_date&limit=6
//
//   base44.entities.ContactSubmissions.create({...})
//     → POST /api/contact
//
// The base URL is read from VITE_API_BASE_URL. In dev you can point that at
// http://localhost:8787 (wrangler dev) or the deployed
// api.topnotch-contractors.com.
//
// TanStack Query keys stay identical to the pre-migration code, so the
// cache behavior is unchanged.

// Build-time API base URL. Now that DNS is on Cloudflare, the production
// API lives at api.topnotch-contractors.com (Worker custom domain).
const API_BASE =
  import.meta.env.VITE_API_BASE_URL ||
  "https://api.topnotch-contractors.com";

// Entity name (Base44 convention) → REST path.
const PATHS = {
  Services: "services",
  PortfolioProjects: "portfolio-projects",
  BlogPosts: "blog-posts",
  AreaPages: "area-pages",
  TeamMembers: "team-members",
  Testimonials: "testimonials",
  ContactSubmissions: "contact",
};

function buildQuery(where, orderBy, limit) {
  const params = new URLSearchParams();
  if (where && typeof where === "object") {
    for (const [k, v] of Object.entries(where)) {
      if (v === undefined || v === null) continue;
      params.set(k, String(v));
    }
  }
  if (orderBy) params.set("orderBy", orderBy);
  if (limit) params.set("limit", String(limit));
  const q = params.toString();
  return q ? `?${q}` : "";
}

async function getJSON(path) {
  const r = await fetch(`${API_BASE}/api/${path}`);
  if (!r.ok) {
    const body = await r.text().catch(() => "");
    throw new Error(`API ${r.status}: ${path} ${body.slice(0, 200)}`);
  }
  return r.json();
}

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

async function postJSON(path, body) {
  const r = await fetch(`${API_BASE}/api/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    let parsed = null;
    try { parsed = JSON.parse(text); } catch {}
    throw new ApiError(`API ${r.status}: ${path}`, r.status, parsed || text);
  }
  return r.json();
}

function makeEntity(name) {
  const path = PATHS[name];
  if (!path) throw new Error(`Unknown entity: ${name}`);

  // Base44 signature: list(orderBy, limit)
  const list = (orderBy, limit) => getJSON(`${path}${buildQuery(null, orderBy, limit)}`);

  // Base44 signature: filter(where, orderBy, limit)
  const filter = (where, orderBy, limit) =>
    getJSON(`${path}${buildQuery(where, orderBy, limit)}`);

  const create = (payload) => postJSON(path, payload);

  return { list, filter, create };
}

const entities = Object.fromEntries(
  Object.keys(PATHS).map((name) => [name, makeEntity(name)]),
);

// Same export name as the old SDK so the import diff in each page is just
// the path: `from "@/api/base44Client"` → `from "@/lib/api"`.
export const base44 = { entities };
export default base44;
